import { mapGetters } from 'vuex';
import * as moment from 'moment';
import Swal from 'sweetalert2';
import api from '@/utils/api';
import Toolbar from '@/components/toolbar';
import LoadingComponent from '@/components/loading-component';
import ServiceLog from '@/utils/service-log-class';
import * as Constants from '@/constants';
import EventBus from '@/event-bus';
import SlStepper from '../service-log-form/service-log-form.vue';
import StudentCard from '../student-card/student-card.vue';

export default {
  components: {
    SlStepper,
    Toolbar,
    StudentCard,
    LoadingComponent
  },
  name: 'CreateSL',
  data() {
    return {
      bottomNav: 'create',
      student: null,
      tab: null,
      loadData: false,
      studentsArray: null,
      provider: '',
      slData: '',
      submitted: false,
      skipValidations: false,
      saveOptions: '',
      needLevelArray: '',
      deliveryModeArray: '',
      showLoading: false,
      errorMsg: '',
      resetValidations: false
    };
  },
  computed: {
    ...mapGetters({
      showTooltip: 'showTooltip'
    }),
    isMobile() {
      return this.$mq === 'mobile';
    },
    isTablet() {
      return this.$mq === 'tablet';
    }
  },
  methods: {
    removeStudent(student) {
      // remove student from the array
      this.studentsArray.splice(this.studentsArray.indexOf(student), 1);
      // remove studentId from localStorage
      // create an array of studentIds
      const newArray = localStorage.getItem('selected-students').split(',').filter((id) => {
        return Number(id) !== (student.serviceId);
      });
      localStorage.setItem('selected-students', newArray);
      EventBus.$emit('reset-validations');
    },
    onRemoveStudentClicked(student) {
      // if there is only one student in the student array, navigate away
      if (this.studentsArray.length === 1) {
        // clear the selected students array
        localStorage.setItem('selected-students', [])
        const subArray = localStorage.getItem('submission-array');
        // navigate away from the page
        if (!subArray || !subArray.length) {
          this.$router.push('/dashboard');
        } else {
          // if there are submissions, take them to the history page
          this.$router.push('/history');
        }
      } else if (this.studentHasChanges(student) && !this.submitted) {
        // there is one more than one student in the array
        // the student being deleted is dirty
        Swal.fire(
          {
            type: 'warning',
            text: 'You have unsaved information.  Are you sure you want to delete this student?',
            showCancelButton: true
          })
          .then((response) => {
            // if ok was clicked
            if (response && response.value) {
              this.removeStudent(student);
            }
          });
      } else {
        // there are multiple students in the array, but no changes have been made
        this.removeStudent(student);
      }
    },
    hasServiceInformationChanged(info) {
      return !!info.needLevel || !!info.sessionTime || !!info.sessionTimeEnd ||
        !!info.sessionMinutes || !!info.locationOther || !!info.deliveryMode || !!info.roomName || !!info.bus;
    },
    hasMedicaidInformationChanged(array) {
      let hasChanges = false;
      for (let i = 0, l = array.length; i < l; i++) {
        if (array[i].minutes || array[i].outcome || (array[i].procedures && array[i].procedures.length)) {
          hasChanges = true;
          break;
        }
      }
      return hasChanges;
    },
    studentHasChanges(student) {
      const serviceInformation = student.form.serviceInformation;
      const medicaidInformation = student.form.medicaid;
      return this.hasServiceInformationChanged(serviceInformation) ||
        this.hasMedicaidInformationChanged(medicaidInformation);
    },
    hasChanges() {
      let hasChanges = false;
      const array = this.studentsArray;
      for (let i = 0, l = array.length; i < l; i++) {
        if (this.studentHasChanges(array[i])) {
          hasChanges = true;
          break;
        }
      }
      return hasChanges;
    },
    addFormDataToStudent(student) {
      return new Promise((resolve, reject) => {
        api.get(Constants.GET_SERVICE_LOCATIONS_URL(student.schoolName))
          .then((res) => {
            const locationObj = res.data.data[0];
            // make a copy of the student
            const copy = Object.assign({}, student);
            const data = {
              serviceStartDate: student.startDate,
              serviceEndDate: student.endDate,
              providerFullName: `${this.provider.firstName} ${this.provider.lastName}`,
              providerCredential: this.provider.credentialName,
              providerCredentialType: this.provider.credentialType,
              needLevel: null,
              sessionDate: moment().format('YYYY-MM-DD'),
              location: locationObj,
              deliveryMode: null,
              roomName: null,
              medicaidSessions: [{
                minutes: null,
                procedures: [],
                outcome: null
              }]
            };
            copy.form = new ServiceLog(data);
            resolve(copy);
          })
          .catch((error) => {
            if (error.response && error.response.data && error.response.data.message) {
              reject(error.response.data);
            } else {
              reject(error);
            }
          });
      });
    },
    setProviderInformation(providerId, providersArray) {
      this.provider = providersArray.filter((provider) => {
        return provider.userId.toString() === providerId;
      })[0];
    },
    setStudentInformation(studentsArray) {
      const studentIDArray = localStorage.getItem('selected-students').split(',');
      return studentsArray.filter((student) => {
        return studentIDArray.includes(student.serviceId.toString());
      });
    }
  },
  beforeRouteLeave(to, from, next) {
    // make sure they arent trying to leave the route before checking if information has changed
    if (!this.submitted) {
      // if no information has changed, let them leave
      if (!this.hasChanges()) {
        next();
      } else {
        Swal.fire(
          {
            type: 'warning',
            text: 'You have unsaved information.  Are you sure you want to leave this page?',
            showCancelButton: true
          })
          .then((response) => {
            // if ok was clicked
            if (response && response.value) {
              next();
            }
          });
      }
    } else {
      // service log is being submitted, allow the page to navigate away
      next();
    }
  },
  mounted() {
    this.showLoading = true;
    const providerId = localStorage.getItem('provider-id');
    if (providerId) {
      Promise.all([
        api.get(Constants.NSL_PROVIDER_LIST_URL('')),
        api.get(Constants.NSL_PROVIDERS_STUDENT_SERVICES_LIST_URL(providerId, '')),
        api.get(Constants.GET_MEDICAID_PROCEDURES_LIST_URL()),
        api.get(Constants.NEED_LEVELS_URL()),
        api.get(Constants.DELIVERY_MODES_URL())
      ])
        .then((res) => {
          // set provider information on this.provider
          this.setProviderInformation(providerId, res[0].data.data);
          // set student information
          const students = this.setStudentInformation(res[1].data.data);
          // set medicaidArray
          this.medicaidArray = res[2].data.data;
          // set needLevel array
          this.needLevelArray = res[3].data.data;
          // set deliveryMode array
          this.deliveryModeArray = res[4].data.data;
          const promises = [];
          // create a promise array to add data to each student
          students.forEach((student) => {
            promises.push(this.addFormDataToStudent(student));
          });
          return Promise.all(promises);
        })
        .then((promiseArrays) => {
          this.studentsArray = promiseArrays;
        })
        .catch((error) => {
          if (error.response && error.response.data && error.response.data.message) {
            this.errorMsg = error.response.data.message;
          } else {
            this.errorMsg = error.message;
          }
        })
        .finally(() => {
          this.loadData = true;
          this.showLoading = false;
        });
    }
  }
};



// WEBPACK FOOTER //
// ./src/components/service-log/form/create-service-log/create-service-log.js